<template>
    <v-container fluid v-if="sessionActive">

        <v-card color="grey darken-3" height="100%" class="pt-0">
            <material-header
                    :loading="activeEngagedLoading"
                    title="Panel health"
                    info="Understand panel health, utilization and more"
                    help="panel-health"
                    icon="mdi-heart-pulse">

                <template slot="buttons">

                    <v-spacer></v-spacer>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission(minPermission)"
                                   @click="refreshClicked()"
                                   v-on="on"
                                   icon>
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Reload data</span>
                    </v-tooltip>

                </template>
            </material-header>

            <v-alert v-if="!hasPermission(minPermission)"
                     class="body-2"
                     type="error"
            >
                Your user account doesn't have sufficient permissions for this feature
            </v-alert>

            <v-card-text v-if="hasPermission(minPermission)" class="pb-0">
                <v-container fluid class="pa-0">

                    <v-row dense>
<!--                        <v-col cols="12">-->
<!--                            <sub-panel-selector :sub-panels="subPanels" :selected="[]"></sub-panel-selector>-->
<!--                        </v-col>-->

                        <!-- HEALTH SCORE -->
                        <v-col cols="12" md="3" sm="12" xs="12">

                            <v-container fluid class="pt-0">
                                <v-row dense>

                                    <v-col cols="12" class="pt-0 text-center">
                                        <dashboard-item title="Health score"
                                                        :max-width="500"
                                                        :height="800"
                                                        color="primary">
                                            <health-score :score="healthScore"></health-score>
                                        </dashboard-item>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>

                        <v-col cols="12" md="5" sm="12" xs="12">

                            <v-container fluid class="pt-0">
                                <v-row dense class="text-right">

                                    <!-- ACTIVE VS ENGAGED -->
                                    <v-col cols="12" class="pt-0 text-center">

                                        <dashboard-item title="Active vs. engaged"
                                                        color="primary">
                                            <active-engaged-chart :data="activeEngagedData"
                                            ></active-engaged-chart>
                                        </dashboard-item>
                                    </v-col>

                                    <!-- SUBSCRIBED CHART -->
                                    <v-col cols="12" class="pt-5 text-center">
                                        <dashboard-item
                                                title="By status"
                                                color="primary">
                                            <status-chart :data="statusData"></status-chart>
                                        </dashboard-item>
                                    </v-col>

                                </v-row>
                            </v-container>
                        </v-col>

                        <v-col cols="12" md="4" sm="12" xs="12">

                            <panel-utilization
                                    :loading="loading"
                                    :spark-values="sparkLineValues.invited"
                                    :spark-labels="sparkLineValues.labels"
                                    :metrics="healthMetrics"
                                    :hint="hintText"
                                    :period="selectedPeriod"
                                    @change="periodChanged"
                            />

                        </v-col>

                    </v-row>

                </v-container>
            </v-card-text>

            <footer-note>
                Report was generated {{humanAge}}
            </footer-note>
        </v-card>

    </v-container>
</template>

<script>

    import MaterialHeader from '../../../components/UI/MaterialHeader';
    import Json from 'vue-json-pretty';
    import Store from '../store';
    import {mapActions, mapMutations, mapState} from 'vuex';
    import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
    import ToolbarPage from '../../../components/UI/ToolbarPage';
    import DashboardTrendValue from '../../../components/UI/DashboardTrendValue';
    import StatBoxCounter from '../../../components/UI/Counter';
    import StatusChart from './PanelistByStatusChart';
    import ActiveEngagedChart from "./ActiveEngagedChart";
    import DashboardItem from "../../../components/UI/DashboardItem";
    import FooterNote from "../../../components/UI/FooterNote";
    import moment from 'moment';
    import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
    import HealthScore from "./HealthScore";
    import PanelUtilization from "./PanelUtilization";
    import PanelGrowth from "../../Dashboard/components/PanelGrowth";
    import SubPanelSelector from "../../../components/UI/SubPanelSelector";

    const vuexModule = 'Health';

    export default {

        mixins: [activeSessionChecker, timeDisplay],

        components: {
            PanelGrowth,
            Json,
            ToolbarPage,
            MaterialHeader,
            DashboardTrendValue,
            StatBoxCounter,
            StatusChart,
            ActiveEngagedChart,
            DashboardItem,
            FooterNote,
            HealthScore,
            PanelUtilization,
            SubPanelSelector,

        },

        data() {
            return {
                minPermission: 'view health reports',
                statusLabels: ['Subscribed', 'Unsubscribed', 'Blacklisted'],
                sparkLineInit: {
                    labels: [],
                    invited: [],
                    opened: [],
                    started: [],
                    profile: [],
                    quota: [],
                    completed: [],
                    terminated: []
                },
                sparkLineValues: {},
                selectedPeriod: 12,
            }
        },

        watch: {
            $route(to, from) {
                if (to.name === 'health') {
                    this.loadData(false);
                }
            }
        },

        computed: {

            humanAge() {
                return this.toHumanFrom(moment.utc(this.createdAt));
            },

            activeEngagedData() {

                return [
                    {
                        name: 'Active panelists',
                        percent: this.activeEngaged.active.count,
                        name2: 'Engaged panelists',
                        percent2: this.activeEngaged.engaged.count
                    },
                    {
                        name: 'Non active panelists',
                        percent: this.activeEngaged.all.count - this.activeEngaged.active.count,
                        name2: 'Non engaged panelists',
                        percent2: this.activeEngaged.all.count - this.activeEngaged.engaged.count,
                    }
                ]
            },

            healthScore() {

                const invitedPercent = this.healthMetrics.invitedPercent;
                const terminatedPercent  = this.healthMetrics.profilePercent + this.healthMetrics.quotaPercent;

                let score = invitedPercent - terminatedPercent;

                if (score > 100) score = 100;

                return score;
            },

            hintText() {
                return 'Report period ' + this.periodModel + " months.";
            },

            statusData() {
                return [
                    {name: "Subscribed", value: this.healthMetrics.panelistSubscribedCount},
                    {name: "Unsubscribed", value: this.healthMetrics.panelistUnsubscribedCount},
                    {name: "Blacklisted", value: this.healthMetrics.panelistBlacklistedCount},
                ]
            },

            // Root store
            ...mapState([
                'activeEngaged',
                'activeEngagedLoading',
            ]),

            ...mapState('SubPanels', [
                'subPanels'
            ]),

            ...mapState('PanelSettings', [
                'settings',
            ]),

            ...mapState(vuexModule, [
                'activityByWeek',
                'healthMetrics',
                'loading',
                'createdAt',
            ]),
        },

        methods: {

            // Root store
            ...mapActions([
                'getActiveEngagedTopline',
            ]),

            ...mapActions('SubPanels',[
                'loadSubPanels'
            ]),

            ...mapActions(vuexModule, [
                'getHealthMetricsTopline',
            ]),

            ...mapMutations(vuexModule, [
                'updatePeriod'
            ]),

            refreshClicked() {
                this.loadData(true);
            },

            periodChanged(value) {
                this.selectedPeriod = value;
                this.loadData(true);
            },

            loadData(force) {

                this.getHealthMetricsTopline({period: this.selectedPeriod, force: force})
                    .then(() => {
                        this.prepareSparkLineValues();
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Oops! Health metrics failed to load!', type: 'error'});
                    });

                this.getActiveEngagedTopline(force)
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Oops! Active engaged failed to load!', type: 'error'});
                    })
            },

            prepareSparkLineValues() {

                this.sparkLineValues = JSON.parse(JSON.stringify(this.sparkLineInit));

                this.activityByWeek.forEach((activity) => {
                    this.sparkLineValues.labels.push(activity.weekNumber);
                    this.sparkLineValues.invited.push(activity.invited);
                    this.sparkLineValues.opened.push(activity.opened);
                    this.sparkLineValues.started.push(activity.started);
                    this.sparkLineValues.profile.push(activity.profile);
                    this.sparkLineValues.quota.push(activity.quota);
                    this.sparkLineValues.completed.push(activity.completed);
                    this.sparkLineValues.terminated.push(activity.terminated);
                });
            },
        },

        mounted() {
            this.loadData();
            this.loadSubPanels();
        },

        created() {
            if (!this.$store.state[vuexModule]) {
                this.$store.registerModule(vuexModule, Store);
            }
        },
    }

</script>

<style scoped>

    .periodSelector {
        max-width: 180px;
    }

</style>
